// Primary-source registry: the official government and statutory-regulator
// authorities FinanceLives's rules are derived from, per country and topic.
//
// WHY THIS EXISTS (E-E-A-T): for a YMYL finance site, the strongest trust signal
// available to a non-credentialed author is TRACEABLE citation to authoritative
// primary sources. Surfacing the exact regulator on every glossary, calculator
// and comparison page (a) lets readers verify figures, (b) demonstrates the
// research is grounded in official publications, and (c) gives each page unique,
// topical outbound context instead of an identical templated footer.
//
// EVERY URL below is an official government / statutory-regulator domain and has
// been verified to resolve to the correct authority. Keep them accurate — a dead
// or wrong "source" link actively HURTS trust. When a jurisdiction has a single
// unified regulator (e.g. BaFin, FINMA), fewer links is CORRECT, not lazy — do
// not pad the list with bodies that don't actually govern the topic.

export interface SourceRef {
  name: string;
  url: string;
}

interface CountrySources {
  authority: SourceRef; // national tax authority — applies to all money topics
  byCategory?: Record<string, SourceRef[]>; // topic-specific regulators, when distinct
}

// Reusable references (kept as consts so the same authority is byte-identical
// everywhere it appears and dedup-by-URL always works).
const US_CFPB: SourceRef = { name: "Consumer Financial Protection Bureau", url: "https://www.consumerfinance.gov" };
const UK_FCA: SourceRef = { name: "Financial Conduct Authority", url: "https://www.fca.org.uk" };
const UK_FSCS: SourceRef = { name: "Financial Services Compensation Scheme", url: "https://www.fscs.org.uk" };
const UK_MONEYHELPER: SourceRef = { name: "MoneyHelper (Money & Pensions Service)", url: "https://www.moneyhelper.org.uk/en" };
const AU_MONEYSMART: SourceRef = { name: "ASIC Moneysmart", url: "https://moneysmart.gov.au" };
const AU_APRA: SourceRef = { name: "Australian Prudential Regulation Authority (APRA)", url: "https://www.apra.gov.au" };
const CA_FCAC: SourceRef = { name: "Financial Consumer Agency of Canada", url: "https://www.canada.ca/en/financial-consumer-agency.html" };
const SG_MAS: SourceRef = { name: "Monetary Authority of Singapore", url: "https://www.mas.gov.sg" };
const SG_MONEYSENSE: SourceRef = { name: "MoneySense (MAS)", url: "https://www.moneysense.gov.sg" };
const IN_RBI: SourceRef = { name: "Reserve Bank of India", url: "https://www.rbi.org.in" };
const NZ_FMA: SourceRef = { name: "Financial Markets Authority", url: "https://www.fma.govt.nz" };
const NZ_SORTED: SourceRef = { name: "Sorted — Retirement Commission (Te Ara Ahunga Ora)", url: "https://sorted.org.nz" };
const DE_BAFIN: SourceRef = { name: "BaFin — Federal Financial Supervisory Authority", url: "https://www.bafin.de" };
const CH_FINMA: SourceRef = { name: "FINMA — Swiss Financial Market Supervisory Authority", url: "https://www.finma.ch" };
const NL_AFM: SourceRef = { name: "AFM — Autoriteit Financiële Markten", url: "https://www.afm.nl" };
const NL_DNB: SourceRef = { name: "De Nederlandsche Bank (DNB)", url: "https://www.dnb.nl" };

const SOURCES: Record<string, CountrySources> = {
  us: {
    authority: { name: "IRS — Internal Revenue Service", url: "https://www.irs.gov" },
    byCategory: {
      retirement: [
        { name: "U.S. Social Security Administration", url: "https://www.ssa.gov" },
        { name: "U.S. Dept. of Labor — EBSA (401(k)/ERISA)", url: "https://www.dol.gov/agencies/ebsa" },
      ],
      investments: [
        { name: "U.S. Securities and Exchange Commission (Investor.gov)", url: "https://www.investor.gov" },
      ],
      loans: [US_CFPB],
      savings: [
        { name: "FDIC — Federal Deposit Insurance Corporation", url: "https://www.fdic.gov" },
        { name: "NCUA — National Credit Union Administration", url: "https://ncua.gov" },
      ],
      insurance: [{ name: "HealthCare.gov (CMS)", url: "https://www.healthcare.gov" }],
    },
  },
  gb: {
    authority: { name: "HM Revenue & Customs (GOV.UK)", url: "https://www.gov.uk/government/organisations/hm-revenue-customs" },
    byCategory: {
      retirement: [
        { name: "GOV.UK — State Pension & Pensions", url: "https://www.gov.uk/browse/working/state-pension" },
        { name: "The Pensions Regulator", url: "https://www.thepensionsregulator.gov.uk" },
      ],
      investments: [UK_FCA, UK_FSCS],
      loans: [UK_FCA, UK_MONEYHELPER],
      savings: [UK_MONEYHELPER, UK_FSCS],
      insurance: [UK_FCA, UK_FSCS],
    },
  },
  au: {
    authority: { name: "Australian Taxation Office (ATO)", url: "https://www.ato.gov.au" },
    byCategory: {
      retirement: [
        { name: "ATO — Superannuation", url: "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super" },
        AU_APRA,
      ],
      // asic.gov.au's WAF answers crawlers with a 404, so every AU page kept
      // getting flagged as linking to a dead site — removed per owner decision;
      // Moneysmart (ASIC's own consumer site) stays as the regulator citation.
      investments: [AU_MONEYSMART],
      loans: [AU_MONEYSMART],
      savings: [AU_MONEYSMART, AU_APRA],
      insurance: [AU_MONEYSMART, AU_APRA],
    },
  },
  ca: {
    authority: { name: "Canada Revenue Agency (CRA)", url: "https://www.canada.ca/en/revenue-agency.html" },
    byCategory: {
      retirement: [
        { name: "Government of Canada — Public Pensions (CPP/OAS)", url: "https://www.canada.ca/en/services/benefits/publicpensions.html" },
      ],
      // securities-administrators.ca sits behind a bot challenge that answers
      // crawlers with a bodyless 307 — the OSC investor hub is the verified-200
      // statutory alternative for the same topic space.
      investments: [{ name: "Ontario Securities Commission — Investors", url: "https://www.osc.ca/en/investors" }],
      loans: [CA_FCAC],
      savings: [
        CA_FCAC,
        { name: "CDIC — Canada Deposit Insurance Corporation", url: "https://www.cdic.ca" },
      ],
      insurance: [
        { name: "OSFI — Office of the Superintendent of Financial Institutions", url: "https://www.osfi-bsif.gc.ca/en" },
        CA_FCAC,
      ],
    },
  },
  sg: {
    authority: { name: "Inland Revenue Authority of Singapore (IRAS)", url: "https://www.iras.gov.sg" },
    byCategory: {
      retirement: [{ name: "CPF Board", url: "https://www.cpf.gov.sg/member" }],
      investments: [SG_MAS, SG_MONEYSENSE],
      loans: [SG_MAS, SG_MONEYSENSE],
      savings: [
        SG_MONEYSENSE,
        { name: "Singapore Deposit Insurance Corporation (SDIC)", url: "https://www.sdic.org.sg" },
      ],
      insurance: [
        SG_MAS,
        { name: "SDIC — Policy Owners' Protection Scheme", url: "https://www.sdic.org.sg" },
      ],
    },
  },
  in: {
    authority: { name: "Income Tax Department, Government of India", url: "https://www.incometax.gov.in/iec/foportal/" },
    byCategory: {
      retirement: [
        { name: "EPFO — Employees' Provident Fund Organisation", url: "https://www.epfindia.gov.in" },
        { name: "PFRDA — Pension Fund Regulatory & Development Authority (NPS)", url: "https://www.pfrda.org.in" },
      ],
      investments: [{ name: "SEBI — Securities and Exchange Board of India", url: "https://www.sebi.gov.in" }],
      loans: [IN_RBI],
      savings: [
        IN_RBI,
        { name: "DICGC — Deposit Insurance & Credit Guarantee Corporation", url: "https://www.dicgc.org.in" },
      ],
      insurance: [{ name: "IRDAI — Insurance Regulatory & Development Authority", url: "https://www.irdai.gov.in" }],
    },
  },
  nz: {
    authority: { name: "Inland Revenue (IRD)", url: "https://www.ird.govt.nz" },
    byCategory: {
      retirement: [
        NZ_SORTED,
        { name: "Retirement Commission (Te Ara Ahunga Ora)", url: "https://retirement.govt.nz" },
      ],
      investments: [NZ_FMA],
      // From 1 July 2026 the FMA (not the Commerce Commission) regulates the
      // Credit Contracts and Consumer Finance Act (CCCFA).
      loans: [NZ_FMA],
      savings: [NZ_SORTED],
      insurance: [NZ_FMA],
    },
  },
  de: {
    authority: { name: "Bundeszentralamt für Steuern (BZSt)", url: "https://www.bzst.de" },
    byCategory: {
      retirement: [{ name: "Deutsche Rentenversicherung", url: "https://www.deutsche-rentenversicherung.de/DRV/DE/Home" }],
      investments: [DE_BAFIN],
      loans: [DE_BAFIN],
      savings: [DE_BAFIN],
      insurance: [DE_BAFIN],
    },
  },
  ch: {
    authority: { name: "Eidgenössische Steuerverwaltung (ESTV)", url: "https://www.estv.admin.ch/de" },
    byCategory: {
      retirement: [{ name: "AHV/IV — Swiss Social Insurance", url: "https://www.ahv-iv.ch/en/" }],
      investments: [CH_FINMA],
      loans: [CH_FINMA],
      savings: [CH_FINMA],
      insurance: [CH_FINMA],
    },
  },
  nl: {
    authority: { name: "Belastingdienst", url: "https://www.belastingdienst.nl/wps/wcm/connect/nl/home/home" },
    byCategory: {
      retirement: [{ name: "Sociale Verzekeringsbank (SVB)", url: "https://www.svb.nl" }],
      investments: [NL_AFM],
      loans: [NL_AFM],
      savings: [NL_AFM, NL_DNB],
      insurance: [NL_AFM, NL_DNB],
    },
  },
};

// Map the various category labels used across the site (calculator categories
// like "loans", glossary categories like "Credit & Debt", "Superannuation",
// comparison categories like "Healthcare Finance") onto the canonical topic keys
// used in `byCategory`.
function normalizeCategory(category?: string): string | null {
  if (!category) return null;
  const c = category.toLowerCase();
  if (c.includes("retire") || c.includes("pension") || c.includes("super")) return "retirement";
  if (c.includes("invest")) return "investments";
  if (c.includes("mortgage") || c.includes("loan") || c.includes("credit") || c.includes("debt") || c.includes("borrow")) return "loans";
  if (c.includes("insur") || c.includes("health")) return "insurance";
  if (c.includes("saving") || c.includes("budget")) return "savings";
  if (c.includes("tax")) return "tax"; // no distinct regulator -> falls back to authority
  return null;
}

/**
 * Primary sources for a page, given its country and (optional) topic category.
 * Returns the national tax authority plus the topic-specific regulator(s) when
 * they exist — deduplicated by URL, authority always first. Typically 1-3
 * authoritative citations. Empty array for unknown countries (callers should
 * render nothing in that case).
 */
export function getSources(country?: string, category?: string): SourceRef[] {
  if (!country) return [];
  const entry = SOURCES[country.toLowerCase()];
  if (!entry) return [];

  const out: SourceRef[] = [entry.authority];
  const topic = normalizeCategory(category);
  if (topic && entry.byCategory && entry.byCategory[topic]) {
    for (const ref of entry.byCategory[topic]) {
      if (!out.some((s) => s.url === ref.url)) out.push(ref);
    }
  }
  return out;
}
